/**
 * CR-7732: the Haulier field on the call-off Detail tab.
 *
 * Everyone who can open the call-off sees which haulier collects it. The
 * customer can also change it, up to and including Dispatched — the same
 * `can_change_haulier` rule the assign-haulier action enforces — which is the
 * only way to change it once Edit Call-off no longer opens.
 */
import { useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff, HaulierOption } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { Button } from '../../ui/button';
import { Loader2, Pencil, Truck } from 'lucide-react';
import { toast } from 'sonner';
import CallOffHaulierSelect from './CallOffHaulierSelect';
import { includeCurrentHaulier } from './useCallOffWizardHaulier';

interface CallOffHaulierFieldProps {
  callOff: CallOff;
  /** True for the call-off's customer, who alone may change the haulier. */
  canEdit: boolean;
  /** Called after a successful change so the modal reloads detail + history. */
  onChanged: () => void;
}

export default function CallOffHaulierField({ callOff, canEdit, onChanged }: CallOffHaulierFieldProps) {
  const [editing, setEditing] = useState(false);
  const [options, setOptions] = useState<HaulierOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState<number | null>(callOff.haulier ?? null);
  // A refusal has to survive the toast: the load can ship between opening the
  // picker and saving, and "the haulier can't be changed once the load is in
  // transit" is the whole explanation for why the panel won't accept the change.
  const [error, setError] = useState<string | null>(null);

  const changeable = canEdit && callOff.can_change_haulier === true;

  const startEditing = async () => {
    setValue(callOff.haulier ?? null);
    setError(null);
    setEditing(true);
    setLoading(true);
    try {
      const res = await callOffAPI.getHaulierOptions();
      setOptions(includeCurrentHaulier(res.data || [], callOff));
    } catch (err: unknown) {
      console.error('Failed to load hauliers:', err);
      toast.error('Failed to load hauliers');
      setEditing(false);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await callOffAPI.assignHaulier(callOff.id, value);
      toast.success(value === null ? 'Haulier removed' : 'Haulier updated');
      setEditing(false);
      onChanged();
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Failed to update haulier');
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-1 text-muted-foreground">Haulier</label>
      {editing ? (
        <div className="space-y-2">
          <CallOffHaulierSelect
            options={options}
            value={value}
            onChange={setValue}
            loading={loading}
            disabled={saving}
          />
          {!loading && options.length === 0 && (
            <p className="text-xs text-muted-foreground">No hauliers are linked to your company yet.</p>
          )}
          {error && (
            <p role="alert" className="text-xs text-red-600">{error}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving || loading || value === (callOff.haulier ?? null)}
            >
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 border border-border rounded-lg bg-muted/30 px-3 py-2 h-[42px]">
          <Truck className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className={`flex-1 truncate font-medium ${callOff.haulier_name ? '' : 'text-muted-foreground'}`}>
            {callOff.haulier_name || 'Not assigned'}
          </span>
          {changeable && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={startEditing}
              aria-label="Change haulier"
            >
              <Pencil className="h-3.5 w-3.5 mr-1" />
              Change
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
